'use es6';

import { CHART_BLUE, CHART_GREEN, CHART_RED } from 'reporting-visualizations/constants/hubStyleTokensColors';
import { ColorSwatch } from 'foundations-components/transitional/components/ColorSwatch';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { fromJS, Map as ImmutableMap } from 'immutable';
import { Fragment, memo, useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { getProperty, getPropertyLabel, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { WATERFALL } from '../../constants/visualizationTypes';
import { getPropertyFormatter } from '../../formatters/propertyFormatter';
import Highchart from '../../highcharts/Highchart';
import { defaultOptions } from '../../highcharts/options/defaultOptions';
import { defaultLegendOptions } from '../../highcharts/options/legendOptions';
import { WaterfallVisualization } from './waterfallRecords';
import { CustomHighchartsTooltip, TooltipHeader, TooltipBody, TooltipSeriesSymbol, TooltipBodyLabel, TooltipBodyListContainer, TooltipBodyValue } from '../../highcharts/options/customTooltipStyles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const type = WATERFALL;
export const record = WaterfallVisualization;
const BREAKDOWN_OPTIONS = {
  START: {
    color: CHART_BLUE
  },
  CREATED: {
    color: CHART_GREEN
  },
  PULLED: {
    color: CHART_GREEN
  },
  INCREASED: {
    color: CHART_GREEN
  },
  DECREASED: {
    color: CHART_RED
  },
  PUSHED: {
    color: CHART_RED
  },
  LOST: {
    color: CHART_RED
  },
  WON: {
    color: CHART_RED
  },
  END: {
    color: CHART_BLUE,
    isSum: true
  }
};
const fixFractions = (value = 0) => Math.abs(value) < 0.01 ? Number(value.toFixed(2)) : value;
const getTooltipFormatter = (dataset, {
  xField,
  yField
}) => function () {
  const {
    y,
    name,
    color
  } = this.point;
  const backgroundColor = color || (y < 0 ? this.series.userOptions.color : this.series.userOptions.upColor);
  const formattedValue = getPropertyFormatter(getProperty(dataset, yField))(fixFractions(y));
  const tooltip = /*#__PURE__*/_jsxs(CustomHighchartsTooltip, {
    children: [/*#__PURE__*/_jsx(TooltipHeader, {
      children: getReferenceLabel(dataset, xField, name)
    }), /*#__PURE__*/_jsx(TooltipBody, {
      children: /*#__PURE__*/_jsxs(TooltipBodyListContainer, {
        children: [/*#__PURE__*/_jsx(TooltipSeriesSymbol, {
          color: backgroundColor
        }), /*#__PURE__*/_jsx(TooltipBodyLabel, {
          children: I18n.text('reporting-visualizations.tooltip.group', {
            group: I18n.SafeString(getPropertyLabel(dataset, yField))
          })
        }), /*#__PURE__*/_jsx(TooltipBodyValue, {
          children: ` ${formattedValue}`
        })]
      })
    })]
  });
  return renderToString(tooltip);
};
const LEGEND_ITEMS = [{
  name: 'pipelineValue',
  color: CHART_BLUE
}, {
  name: 'pipelineValueIncrease',
  color: CHART_GREEN
}, {
  name: 'pipelineValueDecrease',
  color: CHART_RED
}];
const getLegendContent = () => {
  const legend = /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    children: LEGEND_ITEMS.map(({
      name,
      color
    }) => /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ColorSwatch, {
        marginInlineEnd: "100",
        color: color,
        size: 4
      }), /*#__PURE__*/_jsx(Text, {
        marginInlineEnd: "500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `reporting-visualizations.waterfall.${name}`
        })
      })]
    }, name))
  });
  return renderToString(legend);
};
export const getHighchartsOptions = visualization => {
  const dataset = visualization.getIn(['data', 'primary']);
  const xField = visualization.getIn(['layer', 'x', 'field']);
  const yField = visualization.getIn(['layer', 'y', 'field']);
  const seriesData = dataset.get('data').map(row => ImmutableMap(Object.assign({
    name: row.get(xField),
    y: row.get(yField)
  }, BREAKDOWN_OPTIONS[row.get(xField)])));
  const seriesName = getPropertyLabel(dataset, yField);
  const series = fromJS([{
    data: seriesData,
    name: seriesName,
    events: {
      click: visualization.getIn(['layer', 'onClick'])
    }
  }]);
  const xAxis = fromJS({
    type: 'category',
    labels: {
      formatter() {
        return getReferenceLabel(dataset, xField, this.value);
      }
    }
  });
  const yAxis = fromJS({
    min: 0,
    title: {
      text: seriesName
    },
    labels: {
      formatter() {
        return getPropertyFormatter(getProperty(dataset, yField))(this.value);
      }
    }
  });
  const legendOptions = defaultLegendOptions.merge({
    useHTML: true,
    squareSymbol: false,
    symbolWidth: 0,
    labelFormatter: getLegendContent
  });
  return defaultOptions.setIn(['chart', 'type'], 'waterfall').set('series', series).set('xAxis', xAxis).set('yAxis', yAxis).set('legend', legendOptions).setIn(['tooltip', 'formatter'], getTooltipFormatter(dataset, {
    xField,
    yField
  }));
};
const WaterfallViz = ({
  visualization,
  debug = DEFAULT_DEBUG,
  onReportVisualize
}) => {
  const chartOptions = getHighchartsOptions(visualization, debug);
  useEffect(() => {
    debug.set('Highcharts', chartOptions);
  }, [debug, chartOptions]);
  return /*#__PURE__*/_jsx(Highchart, {
    chartOptions: chartOptions,
    onReportVisualize: onReportVisualize
  });
};
export const Render = /*#__PURE__*/memo(WaterfallViz);